import type React from "react";

// Mirrors the backend payload from
// agent_runtime/surfaces/superdoc.py:_handle_clarification_event.
export interface ClarificationData {
  proposal_id?: string;
  question: string;
  options: string[];
  recommended?: string;
}

/**
 * Renders the agent's clarifying question as clickable options, with the
 * recommended one marked and styled primary. Clicking an option answers
 * by sending it as the next chat message (the agent reads it from
 * conversation history). The clinician can also just type a free-form reply.
 */
const ClarificationOptions: React.FC<{
  data: ClarificationData;
  onSendFollowup: (answer: string) => void;
  disabled?: boolean;
}> = ({ data, onSendFollowup, disabled }) => {
  if (!data || !data.options?.length) return null;

  return (
    <div style={styles.container}>
      <div style={styles.options}>
        {data.options.map((opt) => {
          const isRecommended = !!data.recommended && opt === data.recommended;
          return (
            <button
              type="button"
              key={opt}
              disabled={disabled}
              onClick={() => onSendFollowup(opt)}
              style={{
                ...styles.option,
                ...(isRecommended ? styles.optionRecommended : {}),
                ...(disabled ? styles.optionDisabled : {}),
              }}
            >
              {opt}
              {isRecommended && <span style={styles.badge}>Recommended</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: "8px",
  },
  options: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    textAlign: "left",
    borderRadius: "8px",
    border: "1px solid #D1D5DB",
    backgroundColor: "#FFFFFF",
    color: "#1F2937",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  optionRecommended: {
    borderColor: "#4338CA",
    backgroundColor: "#F5F3FF",
    fontWeight: 600,
  },
  optionDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  badge: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#4338CA",
    backgroundColor: "#E0E7FF",
    padding: "2px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
  },
};

export default ClarificationOptions;
